/**
 * Select Component
 * Reusable dropdown select component.
 */

import { forwardRef, type SelectHTMLAttributes } from 'react';
import styles from './Select.module.css';

/**
 * Select option interface.
 */
interface SelectOption {
  label: string;
  value: string;
}

/**
 * Select component props.
 */
interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Select options */
  options: SelectOption[];
  /** Placeholder/default option text */
  placeholder?: string;
  /** Full width select */
  fullWidth?: boolean;
  /** Optional label */
  label?: string;
  /** Error message */
  error?: string;
}

/**
 * Chevron down icon.
 */
const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Reusable Select dropdown component.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, placeholder, fullWidth = false, label, error, className = '', id, ...props }, ref) => {
    const selectId = id || `select-${label?.toLowerCase().replace(/\s/g, '-')}`;

    const containerClasses = [styles.container, fullWidth ? styles.fullWidth : '', className]
      .filter(Boolean)
      .join(' ');

    const selectClasses = [styles.select, error ? styles.selectError : ''].filter(Boolean).join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={selectId} className={styles.label}>
            {label}
          </label>
        )}
        <div className={styles.selectWrapper}>
          <select ref={ref} id={selectId} className={selectClasses} {...props}>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className={styles.chevron}>
            <ChevronDownIcon />
          </span>
        </div>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    );
  }
);

Select.displayName = 'Select';

